import React, { useCallback, useEffect } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useFormikContext } from 'formik';
import useStyles from './MissionFormActions.styles';
import { MissionExact } from '../../../models/Mission';
import { DropdownButton, UploadButton, TextButton } from '../../../../common/components/buttons';
import { Icon } from '../../../../common/components/icons';
import missionAgent from '../../../agents/missionAgent';
import { downloadFile, inputToFormData } from '../../../../common/utils/fileMethods';
import useAgent from '../../../../common/hooks/useAgent';
import Tooltip from '../../../../common/components/Tooltip';
import { Loader } from '../../../../common/components/loaders';

const MissionFormActions = ({ mission, onReuploadSuccess }) => {
  const classes = useStyles();

  const { dirty } = useFormikContext();

  const [validateMissionConfiguration, missionConfiguration, isValidatingMissionConfiguration] = useAgent(missionAgent.validateMissionConfiguration);
  const [exportMission, exportedMission, isExportingMission] = useAgent(missionAgent.exportMission);

  const handleDownloadKml = useCallback(() => exportMission(mission.id), [exportMission, mission.id]);

  const handleUpdateClick = useCallback(
    (event) => {
      const formData = inputToFormData(event, 'file');
      formData.append('missionId', mission.id);
      validateMissionConfiguration(formData);
    },
    [mission.id, validateMissionConfiguration]
  );

  useEffect(() => {
    if (missionConfiguration) {
      toast.success(i18n.t('mission.message.uploadSuccess'));
      if (onReuploadSuccess) onReuploadSuccess(missionConfiguration, mission.id);
    }
  }, [mission.id, missionConfiguration, onReuploadSuccess]);

  useEffect(() => {
    if (exportedMission && !isExportingMission) {
      downloadFile(exportedMission, mission.name, 'kml', () => null);
    }
  }, [exportedMission, isExportingMission, mission.name]);

  const renderDownloadButton = () => (
    <Tooltip key="downloadButton" title={i18n.t('common.userInterface.unsavedModifications')} placement="top" hidden={!dirty}>
      <TextButton id="downloadButton" onClick={handleDownloadKml} disabled={isValidatingMissionConfiguration || dirty || isExportingMission}>
        <Icon icon="download_for_offline" className={classes.textButtonIcon} />
        {i18n.t('mission.button.downloadMissionConfiguration')}
      </TextButton>
    </Tooltip>
  );

  const renderUploadButton = () => (
    <UploadButton key="uploadButton" id="uploadButton" onClick={handleUpdateClick} type=".kml">
      <TextButton component="span" disabled={isValidatingMissionConfiguration || isExportingMission} style={{ width: '100%', 'justify-content': 'flex-start' }}>
        <Icon icon="update" className={classes.textButtonIcon} />
        {i18n.t('mission.button.uploadMissionConfiguration')}
      </TextButton>
    </UploadButton>
  );

  const isLoading = isValidatingMissionConfiguration || isExportingMission;

  return (
    <div className={classes.root}>
      {isLoading && <Loader style={{ width: '4rem' }} size={25} />}

      {mission && mission.id && (
        <DropdownButton
          label={i18n.t('mission.button.updateMission')}
          options={[{ render: renderDownloadButton }, { render: renderUploadButton }]}
          disabled={isLoading}
        />
      )}
    </div>
  );
};

MissionFormActions.propTypes = {
  mission: MissionExact.isRequired,
  onReuploadSuccess: PropTypes.func,
};

MissionFormActions.defaultProps = {
  onReuploadSuccess: undefined,
};

export default React.memo(MissionFormActions);
